import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Editor } from '@tinymce/tinymce-react';
import { Save, Type, Image as ImageIcon, User, Tag, Folder, ArrowLeft } from 'lucide-react';
import AdminNavbar from './AdminNavbar';

const EditPost = ({ token, setAuthToken }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [metaTitle, setMetaTitle] = useState(''); // <-- ADDED
  const [shortDesc, setShortDesc] = useState('');
  const [content, setContent] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState('');

  const [newImage, setNewImage] = useState(null);
  const [currentImagePath, setCurrentImagePath] = useState('');

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const editorRef = useRef(null);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BASE_URL}/backend/api/posts/${id}`)
      .then(res => {
        const post = res.data;
        setTitle(post.title);
        setMetaTitle(post.meta_title || ''); // <-- ADDED
        setShortDesc(post.meta_description);
        setContent(post.content);
        setAuthorName(post.author_name || '');
        setCategory(post.category || '');
        setTags(post.tags || '');
        setCurrentImagePath(post.image_path);
        setIsLoading(false);
      })
      .catch(err => {
        console.error(err);
        alert("Failed to load post data.");
        navigate('/admin/review-posts');
      });
  }, [id, navigate]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    const seoCleanedContent = content.replace(/&nbsp;/g, ' ');

    const formData = new FormData();
    formData.append('title', title);
    formData.append('meta_title', metaTitle); // <-- ADDED
    formData.append('meta_description', shortDesc);
    formData.append('content', seoCleanedContent);
    formData.append('author_name', authorName);
    formData.append('category', category);
    formData.append('tags', tags);

    if (newImage) {
      formData.append('image', newImage);
    }

    try {
      await axios.post(`${process.env.REACT_APP_BASE_URL}/backend/api/admin/posts/${id}`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      alert('Post updated successfully!');
      navigate('/admin/review-posts');
    } catch (error) {
      console.error(error);
      alert('Error updating post.');
    } finally {
      setIsSaving(false);
    }
  };

  // ADDED: Custom Image Upload Handler for TinyMCE
  const handleImageUpload = (blobInfo, progress) => {
    return new Promise(async (resolve, reject) => {
      const formData = new FormData();
      formData.append('image', blobInfo.blob(), blobInfo.filename());

      try {
        const res = await axios.post(`${process.env.REACT_APP_BASE_URL}/backend/api/upload-image`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          },
          onUploadProgress: (progressEvent) => {
            progress((progressEvent.loaded / progressEvent.total) * 100);
          }
        });
        resolve(res.data.url);
      } catch (error) {
        console.error('Upload failed:', error);
        reject('Image upload failed. Ensure the file is under 5MB.');
      }
    });
  };

  if (isLoading) return <div className="min-h-screen pt-32 text-center">Loading editor...</div>;

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <AdminNavbar setAuthToken={setAuthToken} />

      <div className="container mx-auto px-4 max-w-4xl pt-32">

        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-500 hover:text-brand-cyan font-bold mb-6 transition-colors"
        >
          <ArrowLeft size={18} /> Back to Dashboard
        </button>

        <div className="mb-8 flex justify-between items-end">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading font-black text-brand-dark">
              Edit <span className="text-brand-cyan">Article</span>
            </h1>
            <p className="text-gray-600 mt-2 text-lg">Make changes to the live content.</p>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="bg-brand-dark p-6 border-b border-gray-800">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Type size={20} className="text-brand-cyan" /> Post Editor
            </h2>
          </div>

          <form onSubmit={handleUpdate} className="p-8 space-y-8">

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Post Title</label>
              <input
                type="text" required maxLength={60} value={title}
                className="w-full p-4 rounded-xl border border-gray-200 focus:outline-none focus:border-brand-cyan focus:ring-1"
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            <div>
         <div className="flex justify-between items-end mb-2">
           <label className="block text-sm font-bold text-gray-700">SEO Meta Title (Used for URL Slug)</label>
           <span className="text-xs text-gray-400">{metaTitle.length}/60</span>
         </div>
         <input
           type="text" required maxLength={60} value={metaTitle}
           className="w-full p-4 rounded-xl border border-gray-200 focus:outline-none focus:border-brand-cyan focus:ring-1 transition-colors"
           onChange={(e) => setMetaTitle(e.target.value)}
         />
       </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Short Description</label>
              <textarea
                required maxLength={160} rows={3} value={shortDesc}
                className="w-full p-4 rounded-xl border border-gray-200 focus:outline-none focus:border-brand-cyan focus:ring-1 resize-none"
                onChange={(e) => setShortDesc(e.target.value)}
              ></textarea>
            </div>

            {/* TINYMCE EDITOR */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Content</label>
              <Editor
                tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/7.1.1/tinymce.min.js"
                licenseKey="gpl"
                apiKey="b3o6jlpiksf6zr12043m53vq6xxp80wrmwo8tpa8c867f4sa"
                onInit={(_, editor) => (editorRef.current = editor)}
                value={content}
                onEditorChange={(newContent) => setContent(newContent)}
                init={{
                  height: 450,
                  menubar: true,
                  plugins: [
                    "advlist", "autolink", "lists", "link", "image", "charmap",
                    "preview", "anchor", "searchreplace", "visualblocks", "code",
                    "fullscreen", "insertdatetime", "media", "table", "help", "wordcount",
                  ],
                  toolbar:
                    "undo redo | blocks | bold italic underline forecolor backcolor | alignleft aligncenter alignright | bullist numlist | link image media table | removeformat | help | code fullscreen",
                  
                  // ADDED: Allow file uploads and use custom handler
                  image_title: true,
                  automatic_uploads: true,
                  file_picker_types: 'image',
                  images_upload_handler: handleImageUpload
                }}
              />
              <p style={{ fontSize: "12px", color: "gray", marginTop: "6px" }}>
                💡 Tip: Click <strong>Tools &gt; Source code</strong> to edit raw HTML. You can also drag and drop images into the editor.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-8">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <User size={16} className="text-brand-cyan" /> Author Name
                </label>
                <input
                  type="text" required value={authorName}
                  className="w-full p-4 rounded-xl border border-gray-200 focus:outline-none focus:border-brand-cyan focus:ring-1"
                  onChange={(e) => setAuthorName(e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                  <Folder size={16} className="text-brand-cyan" /> Category
                </label>
                <select
                  required value={category}
                  className="w-full p-4 rounded-xl border border-gray-200 focus:outline-none focus:border-brand-cyan focus:ring-1 bg-white appearance-none"
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="" disabled>Select category</option>
                  <option value="Commercial Signage">Commercial Signage</option>
                  <option value="Vehicle Wraps">Vehicle Wraps</option>
                  <option value="Promotional Products">Promotional Products</option>
                  <option value="Industry News">Industry News</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                <Tag size={16} className="text-brand-cyan" /> Tags (comma separated)
              </label>
              <input
                type="text" required value={tags}
                className="w-full p-4 rounded-xl border border-gray-200 focus:outline-none focus:border-brand-cyan focus:ring-1"
                onChange={(e) => setTags(e.target.value)}
              />
            </div>

            <div className="p-6 bg-gray-50 rounded-2xl border border-gray-200">
              <label className="block text-sm font-bold text-gray-700 mb-4">Cover Image (Leave empty to keep current image)</label>
              <div className="flex items-center gap-6">
                {currentImagePath && (
                  <img
                    src={`${process.env.REACT_APP_BASE_URL}/backend/api/image/${currentImagePath}`}
                    alt="Current Cover"
                    className="w-32 h-20 object-cover rounded-lg shadow-sm border border-gray-300"
                  />
                )}
                <input
                  type="file" accept="image/*"
                  className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-bold file:bg-white file:text-brand-dark hover:file:bg-gray-100 cursor-pointer"
                  onChange={(e) => setNewImage(e.target.files[0])}
                />
              </div>
            </div>

            <div className="pt-4 border-t border-gray-100">
              <button
                type="submit" disabled={isSaving}
                className="w-full py-4 bg-brand-cyan text-brand-dark font-bold rounded-xl hover:bg-brand-blue hover:text-white transition-all shadow-md flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSaving ? "Saving Changes..." : <><Save size={20} /> Update Article</>}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default EditPost;